import { useEffect, useMemo, useState } from 'react';
import { db } from '@/offline/db';
import { cn } from '@/lib/cn';
import type { EPCR, Procedure, MedicationGiven } from '@/types';
import {
  HCPCS_CODES, OD_MODIFIERS, buildBillingBundle,
  type LevelOfService, type BillingBundle,
} from '@/lib/billing-catalog';
import { CheckCircle2, AlertTriangle, DollarSign, Ambulance, FileBarChart, Copy } from 'lucide-react';
import { CmnTracker } from '@/components/CmnTracker';
import { UpstreamEcho } from '@/components/UpstreamEcho';
import { useParams } from 'react-router-dom';
import { exportToComstar, checkExportGates } from '@/lib/comstar-service';
import { useAuthStore } from '@/lib/auth-store';
import { Send } from 'lucide-react';

/**
 * BillingTab — RI billing report per addendum § 4.
 *
 * Surfaces:
 *   - Derived level-of-service + suggested base HCPCS code
 *   - Origin-destination modifier pair with auto-selection from
 *     the ePCR's scene/destination data
 *   - Loaded mileage confirmation status
 *   - Denial-prevention checks (patient signature, CMN, mileage,
 *     modifier validity, LOS mismatch)
 *   - The submittable billing bundle with a Copy button
 *
 * Billing Admin role's full revenue cycle dashboard per § 4.8 is a
 * separate page (/admin/billing) — this tab is the per-report view.
 */

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

export function BillingTab({ epcr, onUpdate }: Props) {
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [medications, setMedications] = useState<MedicationGiven[]>([]);
  const [originMod, setOriginMod] = useState<string>('');
  const [destMod, setDestMod] = useState<string>('');
  const [emergency, setEmergency] = useState<boolean>(true);
  const [declaredLos, setDeclaredLos] = useState<LevelOfService | undefined>(undefined);
  const [cmnOnFile, setCmnOnFile] = useState(false);
  const [patientSignatureOnFile, setPatientSignatureOnFile] = useState(false);

  useEffect(() => {
    void (async () => {
      const [procs, meds] = await Promise.all([
        db.procedures.where('epcr_id').equals(epcr.id).toArray(),
        db.medications_given.where('epcr_id').equals(epcr.id).toArray(),
      ]);
      setProcedures(procs);
      setMedications(meds);
      // Check if a patient signature is already captured
      const sigs = await db.signatures
        .where('epcr_id').equals(epcr.id)
        .and((s) => s.signer_role === 'patient' || s.signer_role === 'guardian')
        .toArray();
      setPatientSignatureOnFile(sigs.length > 0);
    })();
  }, [epcr.id]);

  const bundle: BillingBundle = useMemo(() => buildBillingBundle({
    epcr,
    procedures,
    medications,
    emergency,
    modifierOrigin: originMod || null,
    modifierDestination: destMod || null,
    mileageConfirmed: !!epcr.mileage_confirmed_at,
    cmnOnFile,
    patientSignatureOnFile,
    declaredLos,
  }), [epcr, procedures, medications, emergency, originMod, destMod, cmnOnFile, patientSignatureOnFile, declaredLos]);

  const baseCode = HCPCS_CODES.find((c) => c.code === bundle.baseHcpcs);

  function copySummary() {
    const lines = [
      `EMSPCR Billing Bundle`,
      `ePCR: ${epcr.id}`,
      `Base HCPCS: ${bundle.baseHcpcs}  (${baseCode?.label ?? '?'})`,
      bundle.mileageHcpcs ? `Mileage HCPCS: ${bundle.mileageHcpcs} — ${bundle.loadedMiles} mi` : 'Mileage: not yet confirmed',
      `Modifier: ${bundle.combinedModifier ?? '(missing)'}`,
      `Level of Service: ${bundle.levelOfService}${bundle.declaredLos && bundle.declaredLos !== bundle.levelOfService ? ` (declared ${bundle.declaredLos})` : ''}`,
      `Emergency: ${bundle.emergency ? 'Yes' : 'No'}`,
      '',
      'Denial Checks:',
      ...bundle.denialChecks.map((c) => `  [${c.ok ? '✓' : '✗'}] ${c.label}${c.detail ? ` — ${c.detail}` : ''}`),
    ];
    void navigator.clipboard?.writeText(lines.join('\n'));
  }

  return (
    <div className="space-y-3 max-w-4xl">
      {/* Header */}
      <header className="flex items-center justify-between border-b border-slate-800 pb-2">
        <div>
          <h2 className="text-lg font-bold uppercase tracking-tight text-slate-100 flex items-center gap-2">
            <DollarSign size={18} className="text-emerald-400" />
            Billing · Rhode Island
          </h2>
          <p className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
            HCPCS + origin-destination modifiers + denial-prevention checks
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={copySummary} className="btn-secondary text-xs gap-1">
            <Copy size={12} /> Copy Bundle
          </button>
          <ExportToComstarButton epcr={epcr} bundle={bundle} pcsRequired={!emergency} />
        </div>
      </header>

      {/* Captured-once echoes — these fields are entered upstream
          (Patient + Dispatch tabs); this section displays them
          read-only so billing reviewers can confirm the captured
          values without an editable duplicate. Sprint K. */}
      <UpstreamEchoSection epcr={epcr} />

      {/* CMN / PCS tracker — Foundation §10.4. Only renders for
          non-emergency transports. Gates billing submit when the
          CMN is missing / expired. */}
      <CmnTracker epcr={epcr} onUpdate={onUpdate} required={!emergency} />

      {/* Derived LOS card */}
      <section className="border border-slate-700 bg-slate-900/50">
        <SectionHeader icon={<Ambulance size={13} />} title="Level of Service" />
        <div className="p-3 grid gap-3 md:grid-cols-3">
          <Cell label="Derived from documented care" value={bundle.levelOfService} tone="emerald" />
          <div>
            <div className="data-label-dense">Declare (override)</div>
            <select
              className="input-field"
              value={declaredLos ?? ''}
              onChange={(e) => setDeclaredLos(e.target.value ? (e.target.value as LevelOfService) : undefined)}
            >
              <option value="">(use derived: {bundle.levelOfService})</option>
              <option value="BLS">BLS</option>
              <option value="ALS1">ALS-1</option>
              <option value="ALS2">ALS-2</option>
              <option value="SCT">SCT</option>
            </select>
          </div>
          <div>
            <div className="data-label-dense">Emergency Dispatch?</div>
            <div className="flex gap-1 pt-1">
              <PillToggle active={emergency} onClick={() => setEmergency(true)}  label="Yes" />
              <PillToggle active={!emergency} onClick={() => setEmergency(false)} label="No · Non-emerg" />
            </div>
          </div>
        </div>
      </section>

      {/* Base HCPCS */}
      <section className="border border-slate-700 bg-slate-900/50">
        <SectionHeader icon={<FileBarChart size={13} />} title="Base HCPCS Code" />
        <div className="p-3 flex items-baseline gap-3 flex-wrap">
          <span className="font-mono tabular-nums text-3xl font-black text-slate-100">{bundle.baseHcpcs}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-slate-100 font-semibold">{baseCode?.label}</div>
            <div className="text-[11px] text-slate-500 leading-snug mt-0.5">{baseCode?.trigger}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-600 mt-0.5">
              Payer: {baseCode?.payerCoverage}
            </div>
          </div>
        </div>
      </section>

      {/* Origin-Destination modifier */}
      <section className="border border-slate-700 bg-slate-900/50">
        <SectionHeader icon={<FileBarChart size={13} />} title="Origin → Destination Modifier" />
        <div className="p-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <div className="data-label-dense">Origin</div>
              <select className="input-field" value={originMod} onChange={(e) => setOriginMod(e.target.value)}>
                <option value="">—</option>
                {OD_MODIFIERS.map((m) => (
                  <option key={m.char} value={m.char}>{m.char} · {m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="data-label-dense">Destination</div>
              <select className="input-field" value={destMod} onChange={(e) => setDestMod(e.target.value)}>
                <option value="">—</option>
                {OD_MODIFIERS.map((m) => (
                  <option key={m.char} value={m.char}>{m.char} · {m.label}</option>
                ))}
              </select>
            </div>
            <div>
              <div className="data-label-dense">Combined</div>
              <div className={cn(
                'font-mono tabular-nums text-3xl font-black',
                bundle.combinedModifier ? 'text-slate-100' : 'text-slate-600',
              )}>
                {bundle.combinedModifier ?? '—'}
              </div>
            </div>
          </div>
          <p className="mt-2 text-[10px] text-slate-500 leading-snug">
            Most common: <code className="text-slate-300">RH</code> (residence → hospital) and
            <code className="text-slate-300"> SH</code> (scene → hospital). <code className="text-slate-300">HH</code> is not accepted by CMS.
          </p>
        </div>
      </section>

      {/* Non-emergency CMN */}
      {!emergency && (
        <section className="border border-slate-700 bg-slate-900/50">
          <SectionHeader icon={<FileBarChart size={13} />} title="Certificate of Medical Necessity" />
          <label className="flex items-center gap-2 px-3 py-2 text-sm text-slate-200">
            <input type="checkbox" checked={cmnOnFile} onChange={(e) => setCmnOnFile(e.target.checked)} />
            CMN on file for this non-emergency transport
          </label>
        </section>
      )}

      {/* Denial-prevention checklist */}
      <section className="border border-slate-700 bg-slate-900/50">
        <SectionHeader
          icon={<AlertTriangle size={13} />}
          title="Denial-Prevention Checks"
          trailing={
            <div className="text-[10px] font-mono uppercase tracking-widest">
              {bundle.blockerCount > 0
                ? <span className="text-amber-300">{bundle.blockerCount} blocking</span>
                : <span className="text-emerald-300">all blockers pass</span>}
              {bundle.warningCount > 0 && (
                <> · <span className="text-amber-300">{bundle.warningCount} warning</span></>
              )}
            </div>
          }
        />
        <div className="divide-y divide-slate-800">
          {bundle.denialChecks.map((c) => (
            <div key={c.key} className="flex items-start gap-2 px-3 py-1.5">
              {c.ok
                ? <CheckCircle2 size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                : c.severity === 'blocker'
                  ? <AlertTriangle size={14} className="text-amber-400 shrink-0 mt-0.5" />
                  : <AlertTriangle size={14} className="text-blue-400 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn('text-sm', c.ok ? 'text-slate-400' : 'text-slate-100 font-semibold')}>
                    {c.label}
                  </span>
                  {!c.ok && (
                    <span className={cn(
                      'text-[9px] font-mono uppercase tracking-widest',
                      c.severity === 'blocker' ? 'text-amber-300' : 'text-blue-300',
                    )}>
                      {c.severity}
                    </span>
                  )}
                </div>
                {!c.ok && c.detail && (
                  <p className="text-[11px] text-slate-500 leading-snug mt-0.5">{c.detail}</p>
                )}
              </div>
            </div>
          ))}
          {/* Manual attestations — signatures come from another table, but
              a quick check-in here flags it faster than navigating away. */}
          <label className="flex items-center gap-2 px-3 py-2 text-xs text-slate-300">
            <input
              type="checkbox"
              checked={patientSignatureOnFile}
              onChange={(e) => setPatientSignatureOnFile(e.target.checked)}
            />
            I confirm the patient / representative signature is captured on the Signatures tab
          </label>
        </div>
      </section>
    </div>
  );
}

function SectionHeader({
  icon, title, trailing,
}: { icon: React.ReactNode; title: string; trailing?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-slate-700 bg-slate-900 px-2.5 py-1.5">
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-300">
        {icon}
        {title}
      </div>
      {trailing}
    </div>
  );
}

function Cell({ label, value, tone }: { label: string; value: string; tone?: 'emerald' | 'amber' | 'blue' }) {
  const toneCls = tone === 'emerald' ? 'text-emerald-300'
    : tone === 'amber' ? 'text-amber-300'
    : tone === 'blue' ? 'text-blue-300' : 'text-slate-100';
  return (
    <div>
      <div className="data-label-dense">{label}</div>
      <div className={cn('font-mono tabular-nums text-2xl font-black', toneCls)}>{value}</div>
    </div>
  );
}

function PillToggle({ active, onClick, label }: { active: boolean; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-2.5 py-1 text-xs border font-bold uppercase tracking-wider',
        active ? 'bg-blue-600 border-blue-400 text-white' : 'bg-slate-800 border-slate-600 text-slate-300 hover:bg-slate-700',
      )}
    >
      {label}
    </button>
  );
}

/**
 * UpstreamEchoSection — read-only display of captures that are
 * canonical to other stages but referenced by billing. Establishes
 * the "captured once, satisfies all" pattern at the field level
 * (Sprint K). Edit pencil routes back to the canonical tab.
 */
function UpstreamEchoSection({ epcr }: { epcr: EPCR }) {
  const { incidentId, epcrId } = useParams<{ incidentId: string; epcrId?: string }>();
  const base = `/incidents/${incidentId}/epcr/${epcrId ?? epcr.id}`;
  return (
    <section className="space-y-2">
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">
        Captured upstream · read-only
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <UpstreamEcho
          label="Patient name"
          value={[epcr.patient_last_name, epcr.patient_first_name].filter(Boolean).join(', ') || null}
          editPath={`${base}?tab=patient`}
          editLabel="Patient tab"
          locked={epcr.locked}
        />
        <UpstreamEcho
          label="Date of birth"
          value={epcr.patient_dob}
          editPath={`${base}?tab=patient`}
          editLabel="Patient tab"
          locked={epcr.locked}
        />
        <UpstreamEcho
          label="Insurance carrier"
          value={epcr.insurance_carrier}
          editPath={`${base}?tab=patient`}
          editLabel="Patient tab"
          locked={epcr.locked}
        />
        <UpstreamEcho
          label="Primary impression"
          value={epcr.primary_impression}
          editPath={`${base}?tab=dispatch`}
          editLabel="Dispatch tab"
          locked={epcr.locked}
        />
      </div>
    </section>
  );
}

/**
 * ExportToComstarButton — Sprint L. Hard-gates on PCS expiry +
 * blocker-severity denial checks before producing the export.
 *
 * Flow:
 *   - Pre-check gates (denial blockers + PCS expiry).
 *   - On click, call exportToComstar() which:
 *       * builds the canonical CSV row from EPCR + bundle
 *       * either downloads the CSV (manual_download / portal_upload)
 *         OR sandbox-logs to comstar_exports (csv / 837p until the
 *         comstar-export edge function lands)
 *   - Surface result inline.
 */
function ExportToComstarButton({
  epcr, bundle, pcsRequired,
}: {
  epcr: EPCR;
  bundle: BillingBundle;
  pcsRequired: boolean;
}) {
  const { user } = useAuthStore();
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<null | {
    status: 'manual_download' | 'sandbox' | 'sent' | 'failed' | 'rejected';
    msg?: string;
  }>(null);

  // Pre-flight: surface the gate state in the button UI without
  // requiring a click. Operator sees blockers immediately.
  const gate = checkExportGates({ epcr, bundle, pcsRequired });

  async function go() {
    if (!user) return;
    setBusy(true);
    setResult(null);
    try {
      const incident = await db.incidents.get(epcr.incident_id);
      const r = await exportToComstar({
        epcr,
        bundle,
        pcsRequired,
        agencyId: user.agency_id,
        currentUserId: user.id,
        incidentRunNumber: incident?.run_number ?? null,
        incidentDate: incident?.dispatch_time ?? null,
        incidentAddress: incident?.address ?? null,
        incidentCity: incident?.city ?? null,
      });
      setResult({
        status: r.status,
        msg: r.errors?.join('; '),
      });
    } catch (e) {
      setResult({ status: 'failed', msg: e instanceof Error ? e.message : 'Export failed.' });
    } finally {
      setBusy(false);
    }
  }

  const blocked = !gate.ok;

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={go}
        disabled={busy || blocked}
        title={blocked ? gate.blockers.join('\n') : 'Export to Comstar'}
        className={cn(
          'inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-bold min-h-touch',
          blocked
            ? 'bg-rose-200 text-rose-900 border border-rose-400 cursor-not-allowed'
            : 'bg-emerald-700 hover:bg-emerald-800 text-white border border-emerald-800',
        )}
      >
        <Send size={12} />
        {busy ? 'Exporting…' : blocked ? `Blocked (${gate.blockers.length})` : 'Export to Comstar'}
      </button>
      {blocked && (
        <ul className="text-[10px] text-rose-300 max-w-xs text-right space-y-0.5">
          {gate.blockers.map((b, i) => <li key={i}>• {b}</li>)}
        </ul>
      )}
      {result?.status === 'manual_download' && (
        <span className="text-[10px] text-emerald-300">CSV downloaded · log row written</span>
      )}
      {result?.status === 'sandbox' && (
        <span className="text-[10px] text-amber-300">Sandbox · {result.msg ?? 'logged for review'}</span>
      )}
      {result?.status === 'sent' && (
        <span className="text-[10px] text-emerald-300">Posted to Comstar · log row written</span>
      )}
      {result?.status === 'failed' && (
        <span className="text-[10px] text-rose-300">Failed · {result.msg}</span>
      )}
    </div>
  );
}
